import React, { memo, useContext } from 'react';
import { useReframe } from 'nike-re-framejs';
import clsx from 'clsx';
import moment from 'moment';
import { fireGAEvent } from '@/analytics';
import i18n from 'nike-ui/src/common/i18n';
import { CURRENT_PAGE } from 'nike-ui/src/common/routing';
import { BetCount } from '../NikeOneCommon';
import { PrematchMatchOptions } from '@/matchOptions/MatchOptionsButton';
import { formatDate } from '@/common/client';
import BoxOptionsContext from '../context/BoxOptionsContext';
import immutableMemo from '@/common/immutableMemo';
import TicketStatus from '@/scoreboards/views/TicketStatus';
import betInfo from '@/common/betInfo';
import { NIKE1_MATCH } from '@/routing/routes';
import { navigate } from '@/routing';
import { sportEventName, sportEventUrlDescription } from 'nike-ui/src/common/sportEvents';
import { nike1BetExtras } from '@/nikeone/subs';
import { SvgIcon } from 'nike-ui/src/icons/SvgIcon';
import { toSportType } from 'nike-ui/src/common/sports';

function Separator({ score }) {
    if (score) {
        return <div className="separator">{score}</div>;
    }
    return <div className="separator gray">{i18n('common.separator')}</div>;
}

export function Opponents({ home, away, score }) {
    return (
        <>
            <div data-atid="super-home" className={clsx('home ellipsis', { 'home-only': !away })}>
                {home}
            </div>
            {away ? <Separator score={score} /> : null}
            {away ? (
                <div data-atid="super-away" className="away ellipsis">
                    {away}
                </div>
            ) : null}
        </>
    );
}

function getScore(result) {
    if (result && result.get('homeScore') && result.get('awayScore')) {
        return '' + result.get('homeScore') + ':' + result.get('awayScore');
    }
    return null;
}

const MiniScoreboardPrematch = immutableMemo(function MiniScoreboardPrematch({
    sportEvent,
    betId,
    boxStore,
    result,
    disableClick = false,
    info = null
}) {
    const { sportIcon, moreOptions = true } = useContext(BoxOptionsContext);

    if (!sportEvent) {
        return null;
    }
    const sportEventId = sportEvent.get('sportEventId');
    const hasAnalysis = sportEvent.get('articleId');
    const home = sportEvent.getIn(['participants', 0]);
    const away = sportEvent.getIn(['participants', 1]);
    const statisticsId = sportEvent.get('statisticsId');
    const eventName = sportEventName(sportEvent.toJS());
    const urlMeta = sportEventUrlDescription(sportEvent.toJS());

    const openDetail = e => {
        if (disableClick) {
            return;
        }
        e.stopPropagation();
        fireGAEvent(CURRENT_PAGE, eventName, 'Zapas');
        navigate(NIKE1_MATCH, { eventId: sportEventId, meta: urlMeta });
    };

    return (
        <div className="nikeone-match-header">
            <div
                className="super flex items-center"
                onClick={openDetail}
                title={info ? betInfo(info) : ''}
                data-atid="prematch-scoreboard"
            >
                {sportIcon ? <SportIcon sportEvent={sportEvent} /> : null}
                <Opponents home={home} away={away} score={getScore(result)} />
                {moreOptions && (
                    <div className="flex-1 flex items-center justify-end">
                        {hasAnalysis && (
                            <SvgIcon imgId="live-analyse" className="c-black-30 mr-8 mb-3" sizeX="14" sizeY="14" />
                        )}
                        {statisticsId && (
                            <SvgIcon imgId="live-stats" className="c-black-30 mr-8 mb-3" sizeX="14" sizeY="14" />
                        )}
                        <span onClick={openDetail} data-atid="prematch-scoreboard-bet-count">
                            <BetCount betCount={sportEvent.get('betCount')} />
                        </span>
                        <PrematchMatchOptions
                            boxStore={boxStore}
                            eventId={sportEventId}
                            betId={betId}
                            matchNote={sportEvent.get('matchNote')}
                            sportEvent={sportEvent}
                        />
                    </div>
                )}
            </div>
            <div className="score flex">
                <SuperofferIcons boxStore={boxStore} betId={betId} />
                <div data-atid="date-expiration" className="event-data date-expiration">
                    {result || disableClick ? formatDate(sportEvent.get('expiration')) + ' ' : null}
                    {moment(sportEvent.get('expiration')).format('H:mm')}
                </div>
                {!disableClick ? <TicketStatus sportEventId={sportEventId} /> : null}
            </div>
        </div>
    );
});

export default MiniScoreboardPrematch;

const SuperofferIcons = memo(function SuperofferIcons({ boxStore, betId }) {
    const { subscribe } = useReframe();
    const extras = subscribe(nike1BetExtras(boxStore, betId)) || [];
    return (
        <>
            {extras.includes('SUPER_OFFER') && (
                <div data-atid="icon-superponuka" className="icon-default-small icon-default-small-superponuka" />
            )}
            {extras.includes('SUPER_CHANCE') && (
                <div data-atid="icon-supersanca" className="icon-default-small icon-default-small-supersanca" />
            )}
        </>
    );
});

export const SportIcon = React.memo(function SportIcon({ sportId, sportEvent }) {
    const sportName = toSportType(sportEvent ? sportEvent.get('sportId') : sportId);
    return <div data-atid="sport-icon" className={'match-header-icon sport-icon-mini icon-' + sportName} />;
});
